/**
 * Translation (move) handle.
 *
 * Performance optimization: Uses the ODA view.add() pattern from BaseHandle.
 * Ghost entities are added directly to the view (not database) for fast
 * interactive rendering during drag operations.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { TransformContext, KeyPoints } from "../types";
import { BaseHandle } from "./BaseHandle";
import {
  HandleType,
  HandlePosition,
  HANDLE_SIZES,
  TRANSFORM_THEME,
} from "../types";

/**
 * Handle for translating (moving) entities.
 * Relies on BaseHandle's view-based ghost system for performance.
 */
export class TranslationHandle extends BaseHandle {
  readonly type = HandleType.Translation;
  readonly position = HandlePosition.Center;
  protected override tracksMoveAnalytics = true;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityIds: DrawingModule.OdDbObjectId[],
    keyPoints: KeyPoints,
  ) {
    super(viewer, history, entityIds, keyPoints, HandlePosition.Center);

    // Override draw options for translation handle
    this.drawOptions = {
      fillColor: TRANSFORM_THEME.handleFill,
      strokeColor: TRANSFORM_THEME.handleStroke,
      radius: HANDLE_SIZES.translation * HANDLE_SIZES.handleScale,
      icon: "move",
    };
  }

  /**
   * Update ghost positions during drag.
   */
  ongoing(context: TransformContext): void {
    if (!this.isTransforming) return;

    // Calculate translation from handle center so entity center tracks cursor exactly
    const dx = context.worldPoint.x - this.worldPosition.x;
    const dy = context.worldPoint.y - this.worldPosition.y;

    // Update accumulated translation
    this.setAccumulated({ translation: { x: dx, y: dy, z: 0 } });

    // Apply to ghosts (fast - no recordGraphicsModified)
    this.setGhostTransforms();

    // Update view with throttling
    this.throttledUpdate();
  }
}
